import React, { useEffect, useRef, useState } from 'react';
import { ChevronsLeft, ChevronsRight, Info, Save } from 'react-feather';
import { withData } from '../../../DataController';
import { useRemote, useStorage } from '../../../Utils/Utils';
import SidebarExtensionWrapper from './SideBarExtensionWrapper';
import SideBarSection from './SideBarSection';
import DisclosureList from '../../../layouts/AdminPage/SchemesAndFunds/DisclosureList';
import Modal from '../../Modals/Modal';
import FlexWrapper from '../../../layouts/FlexWrapper';
import PageContentToggle from '../../Menus/PageContentToggle';
import { toastDanger } from '../../popups/Toast';
import { numberPins, sameNote } from '../../../Utils/footnotePins';
import './OptionsSidebarExtension.scss';


const NotesSidebarExtension = ({ slideIn, slideOut, template, data, pinSelected, setPinSelected, setShowPinModal, elementsWithSupercript, locatedFootnote, setLocatedFootnote, replaceFootNoteWarning, setReplaceFootNoteWarning }) => {
    const [taggedElements, setTaggedElements] = useState([]);
    const [notes, setNotes] = useState([]);
    const [selectedNotes, setSelectedNotes] = useState([]);
    const [extensionSize, setExtensionSize] = useState('s')
    const [targetToReplace, setTargetToReplace] = useState(null)
    const [newSuperscript, setNewSuperscript] = useState(null)
    const [selectedTab, setSelectedTab] = useState('active')
    const [newSelectedNotes, setNewSelectedNotes] = useState([])
    const [unpinnedNotes, setUnpinnedNotes] = useState(null)
    const [saving, setSaving] = useState(false)
    const savingRef = useRef(false)

    const remote = useRemote()
    const storage = useStorage();

    useEffect(() => {
        if (!template) return;

        const { ticker } = template
        // another fund's notes never show: cleared on a switch, and a reply for the fund left behind is dropped
        let stale = false
        setNotes([])
        if (ticker.type && ticker.type === 'fund' && data && data.tickers && data.tickers[0]) {
            remote.get(`/funds/funds/${data.tickers[0]}`)
                .then((res) => {
                    if (stale || !res || res.length === 0) return
                    setNotes(res.disclosures)
                })
        }
        return () => { stale = true; setPinSelected(null); endElementSelection() }
    }, [template, data.tickers])

    useEffect(() => {
        // a fund without pins has no FOOTNOTES: its pins are none, not the last fund's
        if (data && data.localContentData) {
            const parsedNotes = JSON.parse(data.localContentData.FOOTNOTES || '[]')
            setTaggedElements(parsedNotes)
            setSelectedNotes(parsedNotes.map(note => note.offerNoteId))
        }
    }, [data.localContentData.FOOTNOTES])

    useEffect(() => {
        if (notes) {
            const foundUnpinnedNotes = notes && notes.length > 0 && notes.filter(n => n.selected && !selectedNotes.includes(n.id))
            setUnpinnedNotes(foundUnpinnedNotes)
        }
    }, [notes, selectedNotes])

    useEffect(() => {
        const onKeyDown = (e) => {
            if (e.key === 'Escape' && pinSelected) {
                setShowPinModal(false)
                setPinSelected(null)
                endElementSelection()
            }
        }
        window.addEventListener('keydown', onKeyDown)
        return () => window.removeEventListener('keydown', onKeyDown)
    }, [pinSelected])

    // Resolve the .footnoteTarget <p> regardless of which descendant was clicked.
    // Slate-rendered FootNoteableFixedLabel wraps its content in nested elements,
    // so e.target is usually a <span>/<p> inside the footnote <p>, not the <p> itself.
    const getFootnoteEl = (e) => e?.target?.closest?.('.footnoteTarget')
    const isFootnoteTarget = (e) => !!getFootnoteEl(e)

    // Every pin change saves through here: the pins are numbered as notes, so a note pinned to several places
    // keeps one number (code-plan/qfu/plan-footnoteReuse.md)
    const savePins = (pins) => {
        const numbered = numberPins(pins, elementsWithSupercript)
        data.updateLocalData('FOOTNOTES', JSON.stringify(numbered), 'SIDEBAR', 'FOOTNOTES')
        setTaggedElements(numbered);
        setSelectedNotes(numbered.map(note => note.offerNoteId))
    }

    const onTagElementClick = (e, pinId, pinned) => {
        const footnoteEl = getFootnoteEl(e)
        // A target that already holds a pin, from the saved pins rather than the DOM: a rich-text block draws
        // its superscript inside its text, not as a direct child <sup>, so a DOM check would miss it.
        const existingPin = footnoteEl && taggedElements?.find(note => note.footnoteId === footnoteEl.id && note.superscript)

        if (existingPin && pinId && sameNote(existingPin.offerNoteId, pinId)) {
            // the note is already pinned here
            setPinSelected(null, false)
            endElementSelection()

        } else if (existingPin && pinId) {
            setReplaceFootNoteWarning(true)
            setTargetToReplace(existingPin.footnoteId)
            setNewSuperscript(pinId)

        } else if (footnoteEl || !e) {
            let taggedElementsList;

            if (pinned) {
                taggedElementsList = taggedElements.filter(n => !sameNote(n.offerNoteId, pinId))

            } else if (footnoteEl) {
                // a note pinned elsewhere already may be missing from the fund's notes: its pin has its id and text
                const foundNote = notes?.find(note => note.id === pinId)
                const pinnedNote = taggedElements.find(n => sameNote(n.offerNoteId, pinId))
                if (!foundNote && !pinnedNote) {
                    // the fund's notes are still loading (a fund just switched): end pin mode and say so
                    setPinSelected(null, false)
                    endElementSelection()
                    toastDanger('The fund\'s notes are still loading. Please try again.')
                    return
                }
                const newPinnedNote = {
                    footnoteId: footnoteEl.id,
                    offerNoteId: foundNote ? foundNote.id : pinnedNote.offerNoteId,
                    noteText: foundNote ? foundNote.disclosure : pinnedNote.noteText
                }
                taggedElementsList = [...taggedElements, newPinnedNote]
            }

            savePins(taggedElementsList)
            setPinSelected(null, false)
            endElementSelection()
        }
    }

    // The page's click listener lives across renders (pin mode), so it calls the handler of the latest render:
    // one it closed over would save from pins since changed (a place unpinned meanwhile would come back).
    const tagClickRef = useRef(onTagElementClick)
    tagClickRef.current = onTagElementClick

    // Unpins one place of a note pinned to several
    function onUnpinPlace(noteId, footnoteId) {
        savePins(taggedElements.filter(n => !(sameNote(n.offerNoteId, noteId) && n.footnoteId === footnoteId)))
    }

    function cancelReplace() {
        setPinSelected(null, false)
        setReplaceFootNoteWarning(false)
        endElementSelection()
    }

    function handleTag(pinId, boolean) {
        endElementSelection() // one pin at a time: a listener left from another note would pin it too
        let page = document.getElementById('builder-page-page-wrapper');
        const eventListener = (e) => tagClickRef.current(e, pinId, boolean);
        page.addEventListener('click', eventListener)
        page._tagEventListener = eventListener
    };

    function endElementSelection() {
        let page = document.getElementById('builder-page-page-wrapper');
        if (page?._tagEventListener) {
            page.removeEventListener('click', page._tagEventListener);
            delete page._tagEventListener; // Clean up the reference
        }
    };

    function replaceFootNote() {
        // the new note may be missing from the fund's notes when it is pinned elsewhere: its pin has its text
        const foundNote = notes?.find(note => note.id === newSuperscript)
        const pinnedNote = taggedElements.find(n => sameNote(n.offerNoteId, newSuperscript))
        if (!foundNote && !pinnedNote) return cancelReplace()
        const noteText = foundNote ? foundNote.disclosure : pinnedNote.noteText
        const updatedFootnotes = taggedElements.map(n => {
            if (n.footnoteId === targetToReplace) {
                return { ...n, offerNoteId: newSuperscript, noteText }
            } else return n
        })

        savePins(updatedFootnotes)
        setPinSelected(null, false)
        setReplaceFootNoteWarning(false)
        endElementSelection()
    }

    function expandSideBarRight() {
        if (extensionSize === 's') {
            storage.put('research-master-grid', 'right-m')
            setExtensionSize('m')
        } else if (extensionSize === 'm') {
            storage.put('research-master-grid', 'right-l')
            setExtensionSize('l')
        } else if (extensionSize === 'l') {
            storage.put('research-master-grid', 'sideBarExtRightActive')
            setExtensionSize('s')
        }
    }

    function onPin(pinId, pinned) {
        if (pinned) setPinSelected(null, false, () => onTagElementClick(null, pinId, pinned))
        else if (pinSelected === pinId) {
            setPinSelected(null, false)
            endElementSelection()
        }
        else setPinSelected(pinId, true, () => handleTag(pinId, false))
    }

    // Saves through the footnote-selection endpoint, which takes just the disclosure list. Posting the
    // whole fund back to /funds/funds/{n} fails with a 400: the GET's `clones` is a list, the POST's a map.
    async function updateNotes() {
        if (savingRef.current) return;
        savingRef.current = true; setSaving(true);
        const updateList = notes.map(n => newSelectedNotes.includes(n.id) ? { ...n, selected: true } : n)
        try {
            const savedNotes = await remote.post(`/funds/funds/${data.tickers[0]}/footnotes`, updateList)
            setNotes(savedNotes)
            setNewSelectedNotes([])
        } catch (e) {
            toastDanger(newSelectedNotes.length > 1 ? 'Unable to activate notes' : 'Unable to activate note')
        } finally {
            savingRef.current = false; setSaving(false);
        }
    }

    return (
        <SidebarExtensionWrapper position='right' shadow slideIn={slideIn} slideOut={slideOut} rightExtSize={extensionSize} >
            <SideBarSection
                header={"Note Tags"}
                icon={unpinnedNotes && <Info size={'1rem'} color='#f8dc64' />}
                tooltipMessage={unpinnedNotes && unpinnedNotes.length > 0 && " Unpinned Notes: " + unpinnedNotes.length}
                filters={
                    <FlexWrapper align="center" gap="m">
                        {extensionSize === "l"
                            ? <ChevronsRight className='expand-sidebar-icon' onClick={expandSideBarRight} />
                            : <ChevronsLeft className='expand-sidebar-icon' onClick={expandSideBarRight} />
                        }
                    </FlexWrapper>
                }
            >
                <FlexWrapper flexDirection='row' justify='space-between' className='pb-l'>
                    <PageContentToggle labels={[
                        { label: 'Active', active: selectedTab === 'active', onClick: () => setSelectedTab('active') },
                        { label: 'Inactive', active: selectedTab === 'inactive', onClick: () => setSelectedTab('inactive') },
                    ]} />
                    {selectedTab === 'inactive' && <Save size={16} color={newSelectedNotes.length > 0 && !saving ? '#0E8EFD' : '#ADB1BD'} className={newSelectedNotes.length > 0 && !saving ? 'cursor-pointer' : ''} onClick={() => newSelectedNotes.length > 0 ? updateNotes() : null} />}
                </FlexWrapper>
                <DisclosureList
                    data={notes}
                    activeNotes={selectedTab === 'active' ? true : false}
                    locatedFootnote={locatedFootnote}
                    setLocatedFootnote={setLocatedFootnote}
                    onPin={onPin}
                    onUnpinPlace={onUnpinPlace}
                    pinnedElements={taggedElements}
                    pinSelected={pinSelected}
                    selectedNotes={selectedNotes}
                    newSelectedNotes={newSelectedNotes}
                    setNewSelectedNotes={setNewSelectedNotes}
                />
            </SideBarSection>
            {replaceFootNoteWarning &&
                <Modal
                    header='Replace Tag'
                    cancelButton={{ onClick: () => cancelReplace() }}
                    confirmButton={{ onClick: () => replaceFootNote() }}
                >
                    <p>Are you sure you want to replace this tag?</p>
                </Modal>
            }
        </SidebarExtensionWrapper >
    );
}

export default withData(NotesSidebarExtension);

// Below an example of weird blank space character that was making the Simplicity QFU pdf fail. Undo the commented out text to see the weird blank space
// The NZ Cash Fund provides investors with exposure to debt securities.
